import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subject, Subscription, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { LocationRepository } from "../../../location/location.repository";
import { Location } from "../../../location/location.model";
import { locationSelectDefaultOptions } from "./location-select-default-options.const";
import { locationKind } from "app/location/location-kind.const";
import { AutoCompleteSelectEvent, AutoComplete } from "primeng/autocomplete";
import { LocationIconComponent } from "../../location-icon/location-icon.component";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";
import { Bind } from "primeng/bind";
import { FormsModule, NgModel } from "@angular/forms";

@Component({
  selector: "app-location-select",
  styleUrls: ["./location-select.component.scss"],
  templateUrl: "./location-select.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LocationIconComponent,
    Bind,
    AutoComplete,
    FormsModule,
    AutocompleteEmptyMessageComponent,
  ],
})
export class LocationSelectComponent implements OnDestroy {
  private locationRepository = inject(LocationRepository);

  public readonly locationChange = output<Location>();
  public readonly autocompleteChange = output<boolean>();
  @Input() public location: Location = null;
  @Input() public searchQuery: string;
  public readonly appendTo = input<any>(undefined);
  public readonly clearFiltersButton = input(false);
  public readonly required = input(false);
  @Input() public locationKindFilter = "";
  @Input() public locationKindFilterOptions = locationSelectDefaultOptions;

  public locationKind = locationKind;
  public subscription = new Subscription();
  public locationList: Location[] = [];
  public readonly loading = signal(false);

  private searchTerm = new Subject<{ query: string; kindFilter: string }>();
  private readonly searchModel = viewChild(NgModel);

  public constructor() {
    this.subscription.add(
      this.searchTerm
        .pipe(
          switchMap(({ query, kindFilter }) =>
            this.locationRepository
              .search(query, kindFilter)
              .pipe(catchError(() => of(null))),
          ),
        )
        .subscribe((locations: Location[] | null) => {
          this.locationList = locations ?? [];
          this.loading.set(false);
          this.autocompleteChange.emit(true);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onLocationChange(event: AutoCompleteSelectEvent) {
    if (event.value.active) {
      this.location = event.value;
      this.locationChange.emit(event.value);
      this.searchQuery = "";
    } else {
      event.originalEvent.preventDefault();
      event.originalEvent.stopPropagation();
    }
  }

  // The control is required, so once it has been touched an empty value paints
  // it invalid. Callers that clear it after a successful save reset that.
  public resetTouchedState(): void {
    const control = this.searchModel()?.control;
    control?.markAsUntouched();
    control?.markAsPristine();
  }

  public getPlaceholder() {
    if (this.location && !!this.location.getName()) {
      return this.location.getName();
    }

    return "Please select location";
  }

  public clearLocationKindFilter() {
    this.locationKindFilter = "";
    this.searchQuery = "";
    this.locationKindFilterOptions = locationSelectDefaultOptions;
  }

  public setLocationKindFilter(filter: string) {
    this.locationKindFilter = filter;
  }

  public search(event: { originalEvent: KeyboardEvent; query: string }): void {
    let kindFilter: string;
    if (this.locationKindFilter) {
      kindFilter = this.locationKindFilter;
    } else if (this.locationKindFilterOptions) {
      kindFilter = this.locationKindFilterOptions.join(",");
    } else {
      kindFilter = locationSelectDefaultOptions.join(",");
    }

    // new array reference opens the overlay so the loading message is visible
    this.locationList = [];
    this.loading.set(true);
    this.searchTerm.next({ query: event.query, kindFilter });
  }
}
